import { Add, TrendingDown, TrendingUp, WorkOutline } from "@mui/icons-material";
import { Box, CircularProgress } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useSelector } from "react-redux";

import { ProjectHeader } from "../../components/project";
import {
  OfficeBudgetOverview,
  OfficeDetails,
  OfficeDocuments,
  OfficeExpense,
  OfficeIncome,
  OfficePurchase,
} from "../../components/office";
import { PageHeaderTabs } from "../../components/v3";
import {
  ADD_CLIENT_INCOME,
  CREATE_EXPENSE,
  CREATE_PURCHASE,
  VIEW_EXPENSE,
  VIEW_OFFICE_DASHBOARD,
  VIEW_OFFICE_BUDGET_OVERVIEW,
  VIEW_OFFICE_DOCUMENTS,
} from "../../constants";
import { PageLayout } from "../../layouts/PageLayout";
import { CompanyService } from "../../services/company.service";
import { formatRupees, useHasPermission } from "../../utils";
import { useTabNavigate } from "../../hooks/usePersistedTableState";

const OFFICE_TABS = ["overview", "income", "expense", "purchases"];

export const OfficeOverview = () => {
  const navigate = useNavigate();
  // Tab switches replace the history entry, so Back leaves the page in one step; each tab's table keeps its filters.
  const navigateTab = useTabNavigate();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get("tab");

  const [accountDetails, setAccountDetails] = useState({ name: "" });
  const [accountSummary, setAccountSummary] = useState({});
  const [filters, setFilters] = useState({ isOffice: true });
  const [activeTab, setActiveTab] = useState(currentTab || "overview");
  const [loading, setLoading] = useState(false);
  const { user } = useSelector((state: any) => state.user);

  // TODO: Replace with dedicated office account permissions when available.
  const hasViewOffice = useHasPermission(VIEW_OFFICE_DASHBOARD);
  const hasViewBudget = useHasPermission(VIEW_OFFICE_BUDGET_OVERVIEW);
  const hasViewDocuments = useHasPermission(VIEW_OFFICE_DOCUMENTS);
  const hasAddIncome = useHasPermission(ADD_CLIENT_INCOME);
  const hasViewExpense = useHasPermission(VIEW_EXPENSE);
  const hasAddPurchase = useHasPermission(CREATE_PURCHASE);
  const hasAddExpense = useHasPermission(CREATE_EXPENSE);

  const allTabs = useMemo(
    () => [
      { key: "overview", permission: hasViewOffice },
      { key: "income", permission: hasAddIncome },
      { key: "expense", permission: hasViewExpense },
      { key: "purchases", permission: hasAddPurchase },
    ],
    [hasViewOffice, hasAddIncome, hasViewExpense, hasAddPurchase],
  );

  const allowedTabs = useMemo(() => allTabs.filter((tab) => tab.permission === true).map((tab) => tab.key), [allTabs]);

  useEffect(() => {
    const tabFromUrl = currentTab || "overview";
    setActiveTab(tabFromUrl);
  }, [currentTab]);

  useEffect(() => {
    if (allowedTabs.length === 0) {
      return;
    }

    if (!allowedTabs.includes(activeTab)) {
      const fallbackTab = allowedTabs.includes("overview") ? "overview" : allowedTabs[0];
      setActiveTab(fallbackTab);
      navigate(`/office/overview?tab=${fallbackTab}`, { replace: true });
    }
  }, [activeTab, allowedTabs, navigate]);

  useEffect(() => {
    if (activeTab === "overview" && user?.company?.id) {
      fetchOverviewData();
    }
  }, [activeTab, user?.company?.id, filters]);

  const fetchOverviewData = async () => {
    setLoading(true);

    try {
      const companyId = user?.company?.id;
      if (!companyId) return;

      const [detailsResponse, summaryResponse] = await Promise.all([
        CompanyService.getCompanyDetails(companyId),
        CompanyService.getCompanySummary(filters),
      ]);

      setAccountDetails(detailsResponse.data);
      setAccountSummary(summaryResponse.data);
    } catch (error) {
      console.error("Failed to fetch office account overview:", error);
      toast.error("Failed to fetch office account details");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    if (!allowedTabs.includes(tab)) {
      return;
    }

    setActiveTab(tab);
    navigateTab(`/office/overview?tab=${tab}`);
  };

  const incomeActions = [
    hasAddIncome && {
      icon: <Add />,
      buttonText: "Add Income",
      tooltip: "Add Income",
      onClick: () => navigate("/add-income?accountType=office&tab=income"),
    },
  ].filter(Boolean);

  const expenseActions = [
    hasAddExpense && {
      icon: <Add />,
      buttonText: "Add Expense",
      toolTip: "Add Expense",
      onClick: () => navigate("/expense/add?accountType=office&tab=expense"),
    },
  ].filter(Boolean);

  const purchaseActions = [
    hasAddPurchase && {
      icon: <WorkOutline />,
      buttonText: "Add Purchase",
      toolTip: "Add Purchase",
      onClick: () => navigate("/vendor/purchase/add?accountType=office&tab=purchases"),
    },
  ].filter(Boolean);

  const renderActiveTab = () => {
    switch (activeTab) {
      case "overview":
        return (
          <>
            <ProjectHeader
              title={accountDetails?.name}
              cards={[
                {
                  title: "Total Income",
                  value: formatRupees(accountSummary?.totalIncome),
                  color: "#00C853",
                  icon: <TrendingUp />,
                },
                {
                  title: "Total Expense",
                  value: formatRupees(accountSummary?.totalExpense),
                  color: "#FF1744",
                  icon: <TrendingDown />,
                },
                {
                  title: "Profit",
                  value: formatRupees(accountSummary?.profit),
                  color: "#674BF5",
                  icon: <TrendingUp />,
                },
              ]}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "1fr 1fr",
                },
                width: "100%",
                padding: "11px",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <OfficeDetails accountDetails={accountDetails} />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {hasViewBudget && <OfficeBudgetOverview />}
                {hasViewDocuments && <OfficeDocuments />}
              </Box>
            </Box>
          </>
        );
      case "income":
        return (
          <>
            <ProjectHeader title="Income Details" actionButtons={incomeActions} />
            <OfficeIncome />
          </>
        );
      case "expense":
        return (
          <>
            <ProjectHeader title="Expense Details" actionButtons={expenseActions} />
            <OfficeExpense />
          </>
        );
      case "purchases":
        return (
          <>
            <ProjectHeader title="Purchase Details" actionButtons={purchaseActions} />
            <OfficePurchase />
          </>
        );
      default:
        return null;
    }
  };

  const visibleTabs = allowedTabs.filter((tab) => OFFICE_TABS.includes(tab));
  const showOverviewLoading = activeTab === "overview" && loading;

  return (
    <PageLayout>
      <PageHeaderTabs title="Office Account Overview" tabs={visibleTabs} activeTab={activeTab} onTabChange={handleTabChange} />

      {showOverviewLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <CircularProgress sx={{ color: "#674BF5" }} />
        </Box>
      ) : (
        renderActiveTab()
      )}
    </PageLayout>
  );
};
